import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { parse, getValidInboxSort } from '../../util/urlHelpers';
import {
  getSupportedProcessesInfo,
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
} from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { getMessagesOnlyFilter, getMessagesParticipantRole } from './messagesRole';
import { transitionPrivileged } from '../../util/api';
import { queryAllListingTimeSlots } from '../../util/bookingAvailability';
import * as log from '../../util/log';
import { CUSTOMER_CREATE_BOOKING_TRANSITION } from './inquiryBooking';

const INBOX_PAGE_SIZE = 10;
export { CUSTOMER_CREATE_BOOKING_TRANSITION } from './inquiryBooking';

// ================ Helper functions ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// ================ Slice ================ //

const messagesPageSlice = createSlice({
  name: 'MessagesPage',
  initialState: {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
    conversationSummaries: {},
    createBookingInProgress: null,
    createBookingError: null,
  },
  reducers: {
    clearCreateBookingError: state => {
      state.createBookingError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchOrdersOrSalesError = null;
        state.conversationSummaries = {};
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const { response, conversationSummaries } = action.payload;
        const transactions = response.data.data;
        state.fetchInProgress = false;
        state.transactionRefs = entityRefs(transactions);
        state.pagination = response.data.meta;
        state.conversationSummaries = conversationSummaries;
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = action.payload;
      })
      .addCase(createBookingThunk.pending, (state, action) => {
        state.createBookingInProgress = action.meta.arg.transactionId.uuid;
        state.createBookingError = null;
      })
      .addCase(createBookingThunk.fulfilled, (state, action) => {
        const transactionId = action.meta.arg.transactionId.uuid;
        state.createBookingInProgress = null;
        state.createBookingError = null;
        state.conversationSummaries[transactionId] = {
          ...(state.conversationSummaries[transactionId] || {}),
          availableTransitionNames: [],
        };
      })
      .addCase(createBookingThunk.rejected, (state, action) => {
        state.createBookingInProgress = null;
        state.createBookingError = action.payload || action.error;
      });
  },
});

export default messagesPageSlice.reducer;

// ================ Load data ================ //

const loadDataPayloadCreator = (
  { params, search },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  const { tab } = params;

  if (tab !== 'orders' && tab !== 'sales') {
    return Promise.reject(new Error(`Invalid tab for MessagesPage: ${tab}`));
  }

  const currentUser = getState().user?.currentUser;
  const participantRole = getMessagesParticipantRole(tab, currentUser);
  const onlyFilter = getMessagesOnlyFilter(participantRole);

  const { page = 1, sort } = parse(search);
  const processNames = getSupportedProcessesInfo().map(p => p.name);

  const apiQueryParams = {
    only: onlyFilter,
    processNames,
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'lastMessageAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
    ],
    'fields.listing': [
      'title',
      'price',
      'availabilityPlan',
      'publicData.listingType',
      'publicData.unitType',
      'publicData.services',
      'publicData.currency',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
    ...getValidInboxSort(sort || 'lastMessageAt'),
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(async response => {
      dispatch(addMarketplaceEntities(response));

      const transactions = response?.data?.data || [];
      const canQueryMessages = typeof sdk.messages?.query === 'function';
      const canQueryTransitions = typeof sdk.processTransitions?.query === 'function';
      const summaries = canQueryMessages
        ? await Promise.all(
            transactions.map(async transaction => {
              const transactionId = transaction.id;
              const isCustomerBookingInquiry =
                participantRole === TX_TRANSITION_ACTOR_CUSTOMER &&
                !!transaction.attributes?.protectedData?.bookingInquiry;

              try {
                const [messageResult, transitionResult] = await Promise.all([
                  sdk.messages
                    .query({
                      transaction_id: transactionId,
                      include: ['sender'],
                      page: 1,
                      perPage: 1,
                      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
                    })
                    .then(messageResponse => ({ messageResponse }))
                    .catch(() => ({ messageResponse: null })),
                  isCustomerBookingInquiry && canQueryTransitions
                    ? sdk.processTransitions
                        .query({ transactionId })
                        .then(transitionResponse => ({ transitionResponse }))
                        .catch(() => ({ transitionResponse: null }))
                    : Promise.resolve({ transitionResponse: null }),
                ]);
                const messageResponse = messageResult.messageResponse;
                const latestMessage = messageResponse
                  ? denormalisedResponseEntities(messageResponse)[0] || null
                  : null;
                const totalMessages = messageResponse?.data?.meta?.totalItems ?? null;
                const availableTransitionNames = (
                  transitionResult.transitionResponse?.data?.data || []
                )
                  .map(transition => transition?.attributes?.name)
                  .filter(Boolean);

                return [
                  transactionId.uuid,
                  { latestMessage, totalMessages, availableTransitionNames },
                ];
              } catch (error) {
                // A summary is enhancement data. Keep the real transaction row available if
                // an individual message query fails or is not permitted for a legacy thread.
                return [
                  transactionId.uuid,
                  { latestMessage: null, totalMessages: null, availableTransitionNames: [] },
                ];
              }
            })
          )
        : [];

      return { response, conversationSummaries: Object.fromEntries(summaries) };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const loadDataThunk = createAsyncThunk('MessagesPage/loadData', loadDataPayloadCreator);

export const { clearCreateBookingError } = messagesPageSlice.actions;

// Backward compatible wrapper for the thunk
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  return dispatch(loadDataThunk({ params, search }));
};

export const fetchCreateBookingTimeSlots = params => (_dispatch, _getState, sdk) =>
  queryAllListingTimeSlots(sdk, params);

const createBookingPayloadCreator = (
  { transactionId, bookingStart, bookingEnd, price, protectedData },
  { dispatch, rejectWithValue }
) => {
  const bodyParams = {
    id: transactionId,
    transition: CUSTOMER_CREATE_BOOKING_TRANSITION,
    params: {
      bookingStart,
      bookingEnd,
      bookingDisplayStart: bookingStart,
      bookingDisplayEnd: bookingEnd,
      protectedData,
    },
  };
  const orderData = {
    currency: price.currency,
  };
  const queryParams = {
    include: ['booking', 'listing', 'provider', 'customer'],
    expand: true,
  };

  return transitionPrivileged({
    isSpeculative: false,
    orderData,
    bodyParams,
    queryParams,
  })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const transaction = denormalisedResponseEntities(response)[0];
      return { transactionId: transaction.id.uuid };
    })
    .catch(error => {
      log.error(error, 'customer-create-booking-from-inquiry-failed', {
        transactionId: transactionId.uuid,
      });
      return rejectWithValue(storableError(error));
    });
};

export const createBookingThunk = createAsyncThunk(
  'MessagesPage/createBooking',
  createBookingPayloadCreator,
  {
    condition: (_params, { getState }) => !getState().MessagesPage.createBookingInProgress,
  }
);

export const createBookingFromInquiry = params => dispatch =>
  dispatch(createBookingThunk(params)).unwrap();
